/**
 * Cartao "Prontidao para ligar os motores": percentual, barra, o proximo
 * passo em destaque e o checklist item a item, cada um com atalho para a aba
 * e a secao onde se resolve.
 *
 * Decisoes nao obvias:
 *  - Fica ACIMA das abas, sempre visivel: e a resposta para "o que falta para
 *    isto funcionar?", a pergunta de quem abre Configuracoes pela primeira vez.
 *  - Quando tudo esta pronto o cartao ENCOLHE para uma linha com o checklist
 *    dentro de um `<details>`: a empresa que ja opera nao precisa de sete
 *    linhas verdes ocupando a dobra todo dia.
 *  - Estado e texto + icone + cor (RNF16): "ok", "pendente" e "informativo"
 *    tem glifo e rotulo proprios; a cor so acompanha.
 *  - Os numeros do detalhe vem prontos da regra pura (prontidao.ts). Este
 *    componente nao calcula nada.
 */

import { Badge, Button, Icon, ProgressBar, SectionCard, type BadgeTone, type IconName } from '../../../components/ui';
import { formatCount } from '../../../lib/format';
import type { EstadoDoItem, ItemDeProntidao, Prontidao } from '../prontidao';
import type { AbaConfiguracoes } from '../types';
import { ABA_LABEL } from '../vocabulario';

const ESTADO: Readonly<Record<EstadoDoItem, { readonly label: string; readonly tone: BadgeTone; readonly icon: IconName; readonly borda: string; readonly cor: string }>> = {
  ok: { label: 'Pronto', tone: 'good', icon: 'check', borda: 'border-status-good/40', cor: 'text-status-good' },
  pendente: { label: 'Pendente', tone: 'warning', icon: 'alerta', borda: 'border-status-warning/50', cor: 'text-status-warning' },
  informativo: { label: 'Não entra na conta', tone: 'neutral', icon: 'info', borda: 'border-line', cor: 'text-ink-subtle' },
};

function Item({ item, onIr }: { readonly item: ItemDeProntidao; readonly onIr: (aba: AbaConfiguracoes, ancora: string) => void }): JSX.Element {
  const e = ESTADO[item.estado];
  return (
    <li className={`flex h-full flex-col gap-2 rounded-xl border bg-elevated px-4 py-3 ${e.borda}`}>
      <div className="flex items-start justify-between gap-2">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-ink">
          <Icon name={e.icon} size="sm" className={e.cor} />
          {item.titulo}
        </h3>
        <Badge tone={e.tone}>{e.label}</Badge>
      </div>
      <p className="flex-1 text-xs leading-relaxed text-ink-muted">{item.detalhe}</p>
      {item.proximoPasso !== null ? <p className="text-xs leading-relaxed text-ink">{item.proximoPasso}</p> : null}
      {item.estado === 'pendente' ? (
        <div>
          <Button variant="secondary" size="sm" iconRight="seta-dir" onClick={() => onIr(item.aba, item.ancora)}>
            Resolver em {ABA_LABEL[item.aba].label}
          </Button>
        </div>
      ) : null}
    </li>
  );
}

export function ProntidaoDosMotores({
  prontidao,
  onIr,
}: {
  readonly prontidao: Prontidao;
  readonly onIr: (aba: AbaConfiguracoes, ancora: string) => void;
}): JSX.Element {
  const { itens, total, concluidos, percentual, pronto, proximo } = prontidao;
  const resumo = `${formatCount(concluidos)} de ${formatCount(total)} passos concluídos`;
  const tom: BadgeTone = pronto ? 'good' : percentual >= 50 ? 'warning' : 'critical';

  if (pronto) {
    return (
      <details id="prontidao" className="group rounded-2xl border border-status-good/40 bg-surface shadow-soft">
        <summary className="flex cursor-pointer list-none flex-wrap items-center justify-between gap-3 px-5 py-4 text-sm [&::-webkit-details-marker]:hidden">
          <span className="flex items-center gap-3">
            <span aria-hidden="true" className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-status-good/10 text-status-good">
              <Icon name="raio" size="sm" />
            </span>
            <span>
              <span className="block font-semibold text-ink">Motores prontos para ligar</span>
              <span className="block text-xs text-ink-muted">{resumo}. Evento aceito, segredo válido, WhatsApp e voz no ar, funil ligado e equipe dentro.</span>
            </span>
          </span>
          <span className="flex items-center gap-2 text-xs text-ink-subtle">
            <Badge tone="good" icon="check">
              100%
            </Badge>
            <span className="inline-flex items-center gap-1">
              ver checklist
              <Icon name="chevron" size="xs" className="transition-transform group-open:rotate-180" />
            </span>
          </span>
        </summary>
        <ol className="grid gap-3 border-t border-line px-5 py-4 md:grid-cols-2 xl:grid-cols-4">
          {itens.map((i) => (
            <Item key={i.key} item={i} onIr={onIr} />
          ))}
        </ol>
      </details>
    );
  }

  return (
    <SectionCard
      id="prontidao"
      title="Prontidão para ligar os motores"
      icon="raio"
      description="O que já está no lugar e o que ainda falta para a régua sair de verdade. Lido da configuração salva, não do que está sendo digitado."
      aside={
        <Badge tone={tom} dot>
          {percentual}% · {resumo}
        </Badge>
      }
    >
      <ProgressBar value={concluidos} max={total} tone={tom} label="Passos concluídos" className="mb-4" />

      {proximo !== null ? (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-brand/40 bg-brand/5 px-4 py-3">
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-brand">Próximo passo</p>
            <p className="text-sm font-semibold text-ink">{proximo.titulo}</p>
            <p className="text-xs text-ink-muted">{proximo.proximoPasso ?? proximo.detalhe}</p>
          </div>
          <Button variant="primary" iconRight="seta-dir" onClick={() => onIr(proximo.aba, proximo.ancora)}>
            Ir para {ABA_LABEL[proximo.aba].label}
          </Button>
        </div>
      ) : null}

      <ol className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        {itens.map((i) => (
          <Item key={i.key} item={i} onIr={onIr} />
        ))}
      </ol>
    </SectionCard>
  );
}
